/**
 * Layer 0 (Hooks) building block for the FilteredListPageLayout.HeaderActions
 * slot — see `core-ux#2238` for the layered model. Given a list of header
 * actions and a breakpoint at which to collapse them, returns the split
 * (always-inline vs. collapsible vs. overflow) plus the viewport hints a
 * consumer needs to wrap them in CSS visibility helpers (Primer `<Hidden>`
 * or the bundled `.HiddenAt…` classes from
 * `FilteredListPageLayout.module.css`).
 *
 * Why pure CSS instead of JS viewport detection: `useResponsiveValue` is
 * deprecated in Primer and has poor SSR support. Rendering both variants
 * and hiding via CSS keeps the slot SSR-safe and avoids a layout shift
 * on hydration.
 *
 * Both the styled slot (`FilteredListPageLayout.HeaderActions`) and any
 * custom-markup consumer share this hook so the responsive contract is
 * defined in exactly one place.
 */
import {ActionList, Button} from '@primer/react'
import {useMemo, type ComponentType, type ReactNode} from 'react'

/** A viewport breakpoint name matching Primer's `viewportRanges`. */
export type ResponsiveViewport = 'narrow' | 'regular' | 'wide'

/**
 * The verbose action shape used by {@link FilteredListPageLayout.HeaderActions}.
 *
 * Provides two render functions so the inline button and overflow-menu item
 * can be styled differently (e.g. primary `<Button variant="primary">` inline
 * vs. plain `<ActionList.Item>` inside the kebab) without forcing the
 * consumer to duplicate the action's data outside the array.
 *
 * Prefer the simpler {@link SimpleHeaderAction} shape (`label` + `onClick` /
 * `href` + optional `leadingVisual` / `primary`) for the common case; reach
 * for `ResponsiveHeaderAction` only when you need fully custom markup (e.g.
 * a counter badge, a custom button variant, or an action backed by a
 * library component).
 */
export type ResponsiveHeaderAction = {
  /** Stable React key for the action. Used directly as the `key` prop. */
  key: string
  /** Render the action when it appears inline in the header. */
  renderInline: () => ReactNode
  /** Render the action when it appears inside the overflow kebab menu. */
  renderMenuItem: () => ReactNode
  /**
   * When true, keep the action inline regardless of viewport. Use this for
   * the primary CTA (e.g. "New issue") so it stays accessible without
   * opening the menu.
   */
  alwaysInline?: boolean
}

/**
 * The shorthand header-action shape — covers the common case of "button with
 * a label and click handler". Auto-renders the inline `<Button>` and overflow
 * `<ActionList.Item>` so consumers only state the data once.
 *
 * For anything fancier (custom button variant, counter badge, popover anchor,
 * etc.) drop down to {@link ResponsiveHeaderAction} and supply your own
 * `renderInline` / `renderMenuItem`.
 */
export type SimpleHeaderAction = {
  /** Stable React key for the action. Used directly as the `key` prop. */
  key: string
  /** Visible button text. Also used as the menu item label. */
  label: string
  /**
   * Render as `<Button variant="primary">` inline. Primary actions also
   * default to `alwaysInline: true` so the CTA stays accessible without
   * opening the kebab — set `alwaysInline: false` explicitly to override.
   */
  primary?: boolean
  /**
   * Keep this action inline regardless of viewport. Defaults to `true` when
   * `primary` is set, otherwise `false`.
   */
  alwaysInline?: boolean
  /**
   * Optional leading icon rendered before the label. Same component goes
   * to both the inline `<Button leadingVisual>` and the menu
   * `<ActionList.LeadingVisual>` so the action looks identical in both.
   */
  leadingVisual?: ComponentType<{className?: string}>
  /** Click handler. Fired for both the inline button and the menu item. */
  onClick?: () => void
  /**
   * Render as an `<a>` link instead of a `<button>`. Mutually exclusive with
   * `onClick` in HTML semantics; if both are provided, `onClick` still fires
   * (handy for analytics) but the navigation comes from `href`.
   */
  href?: string
  /** Disable both the inline button and the menu item. */
  disabled?: boolean
}

/**
 * Union of the two supported action shapes accepted by
 * {@link useHeaderActionsOverflow} and `FilteredListPageLayout.HeaderActions`.
 *
 * - {@link SimpleHeaderAction} — shorthand for the common case
 * - {@link ResponsiveHeaderAction} — escape hatch for fully custom markup
 */
export type HeaderAction = SimpleHeaderAction | ResponsiveHeaderAction

export type UseHeaderActionsOverflowArgs = {
  /** Ordered list of header actions. Order is preserved in both inline and menu render. */
  actions: HeaderAction[]
  /**
   * Viewport at and below which secondary (non-`alwaysInline`) actions move
   * into the overflow menu. Defaults to `'narrow'` (collapse on phones).
   * `'regular'` collapses at both narrow and regular viewports, leaving
   * actions inline only on wide displays.
   */
  collapseAt?: 'narrow' | 'regular'
}

export type UseHeaderActionsOverflowResult = {
  /**
   * All normalized actions in the original input order. Each item carries
   * its own `alwaysInline` flag so consumers can decide per-item visibility
   * without splitting the list — this is what `FilteredListPageLayout.HeaderActions`
   * iterates so DOM order matches the input array (primary CTA stays on the
   * right when listed last, etc.).
   *
   * Prefer this over the `alwaysInlineActions`/`collapsibleInlineActions`
   * pair when rendering — those buckets are kept for callers that need the
   * pre-split view (e.g. analytics, debugging).
   */
  inlineActions: ResponsiveHeaderAction[]
  /** Actions with `alwaysInline: true`, in their original input order. */
  alwaysInlineActions: ResponsiveHeaderAction[]
  /**
   * Secondary actions that render inline above the collapse breakpoint, in
   * their original input order. Wrap each in a visibility helper that hides
   * at the `overflowVisibleAt` viewports (or iterate `inlineActions` and
   * gate per item).
   */
  collapsibleInlineActions: ResponsiveHeaderAction[]
  /**
   * The same secondary actions, presented for the overflow menu. Renders to
   * an empty array when there's nothing to collapse — consumers can skip the
   * kebab entirely in that case.
   */
  overflowActions: ResponsiveHeaderAction[]
  /**
   * Viewports at which the inline secondary actions are visible. Pair with
   * `<Hidden when={overflowVisibleAt}>` to hide them at the collapse
   * breakpoint(s).
   */
  inlineVisibleAt: ResponsiveViewport[]
  /**
   * Viewports at which the overflow menu is visible. Pair with
   * `<Hidden when={inlineVisibleAt}>` to hide it everywhere else.
   */
  overflowVisibleAt: ResponsiveViewport[]
}

const VIEWPORTS: readonly ResponsiveViewport[] = ['narrow', 'regular', 'wide']

function isResponsiveHeaderAction(action: HeaderAction): action is ResponsiveHeaderAction {
  return typeof (action as ResponsiveHeaderAction).renderInline === 'function'
}

/**
 * Normalize a {@link SimpleHeaderAction} into the verbose
 * {@link ResponsiveHeaderAction} shape with auto-generated `renderInline` /
 * `renderMenuItem`. Exposed for tests and adventurous consumers; everything
 * inside this module funnels through here so the rendering rules live in one
 * place.
 */
export function normalizeHeaderAction(action: HeaderAction): ResponsiveHeaderAction {
  if (isResponsiveHeaderAction(action)) return action

  const {key, label, primary, alwaysInline, leadingVisual: LeadingVisual, onClick, href, disabled} = action

  const variant = primary ? 'primary' : 'default'
  const leadingVisualProp = LeadingVisual ? {leadingVisual: LeadingVisual} : undefined

  return {
    key,
    alwaysInline: alwaysInline ?? primary ?? false,
    renderInline: () =>
      href ? (
        <Button as="a" href={href} variant={variant} onClick={onClick} disabled={disabled} {...leadingVisualProp}>
          {label}
        </Button>
      ) : (
        <Button variant={variant} onClick={onClick} disabled={disabled} {...leadingVisualProp}>
          {label}
        </Button>
      ),
    renderMenuItem: () => {
      // ActionList.LinkItem renders as an `<a>` so href-only actions
      // navigate from the overflow menu (matching the inline `<Button as="a">`
      // behavior). Disabled links fall back to ActionList.Item because
      // LinkItem has no `disabled` prop — it would still navigate.
      // ActionList.LeadingVisual must be inlined as a direct child of
      // Item/LinkItem (enforced by primer-react/direct-slot-children).
      return href && !disabled ? (
        <ActionList.LinkItem href={href} onClick={onClick}>
          {LeadingVisual ? (
            <ActionList.LeadingVisual>
              <LeadingVisual />
            </ActionList.LeadingVisual>
          ) : null}
          {label}
        </ActionList.LinkItem>
      ) : (
        <ActionList.Item disabled={disabled} onSelect={onClick}>
          {LeadingVisual ? (
            <ActionList.LeadingVisual>
              <LeadingVisual />
            </ActionList.LeadingVisual>
          ) : null}
          {label}
        </ActionList.Item>
      )
    },
  }
}

/**
 * Pure transformation that normalizes an action list and splits it into the
 * always-inline, collapsible, and overflow buckets, while also returning the
 * full flattened list (`inlineActions`) in original order. Exposed alongside
 * the hook so unit tests and consumers who only need the data shape (no
 * React identity) can call it without a renderer.
 */
export function computeHeaderActionsOverflow({
  actions,
  collapseAt = 'narrow',
}: UseHeaderActionsOverflowArgs): UseHeaderActionsOverflowResult {
  const inlineActions = actions.map(normalizeHeaderAction)
  const alwaysInlineActions = inlineActions.filter(a => a.alwaysInline === true)
  const collapsibleInlineActions = inlineActions.filter(a => a.alwaysInline !== true)

  const overflowVisibleAt: ResponsiveViewport[] = collapseAt === 'regular' ? ['narrow', 'regular'] : ['narrow']
  const overflowSet = new Set<ResponsiveViewport>(overflowVisibleAt)
  const inlineVisibleAt = VIEWPORTS.filter(v => !overflowSet.has(v))

  return {
    inlineActions,
    alwaysInlineActions,
    collapsibleInlineActions,
    overflowActions: collapsibleInlineActions,
    inlineVisibleAt,
    overflowVisibleAt,
  }
}

/**
 * React hook wrapper around {@link computeHeaderActionsOverflow}. Memoizes
 * the result against the input array reference + `collapseAt` so children
 * downstream don't re-render on every parent render.
 */
export function useHeaderActionsOverflow(args: UseHeaderActionsOverflowArgs): UseHeaderActionsOverflowResult {
  const {actions, collapseAt} = args
  return useMemo(() => computeHeaderActionsOverflow({actions, collapseAt}), [actions, collapseAt])
}
